import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Sun, Moon } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";

export default function Nav() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [theme, setTheme] = useState(() => {
    if (typeof document === "undefined") return "dark";
    return document.documentElement.getAttribute("data-theme") || "dark";
  });

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    try {
      localStorage.setItem("loulou_theme", theme);
    } catch (e) {
      // Ignore localStorage errors
    }
  }, [theme]);

  const toggleTheme = () => setTheme((t) => (t === "dark" ? "light" : "dark"));

  // Check if current path is active
  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 border-b border-stone-200 bg-[#F9F9F6]/95 backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-12">
        <Link to="/" data-testid="nav-logo" className="flex items-baseline gap-2">
          <span className="font-display text-2xl font-medium tracking-tight text-stone-900">Loulou</span>
          <span className="text-xs uppercase tracking-[0.24em] text-stone-500">fitness</span>
        </Link>
        
        {/* Show navigation only for non-members or admins */}
        {(!user || user.role === "admin") && (
          <nav className="hidden items-center gap-10 md:flex">
            <a href="/#plans" data-testid="nav-plans" className="text-sm text-stone-600 transition-colors hover:text-stone-900">Forfaits</a>
            <a href="/#pourquoi" data-testid="nav-pourquoi" className="text-sm text-stone-600 transition-colors hover:text-stone-900">Pourquoi nous</a>
            <a href="/#horaires" data-testid="nav-horaires" className="text-sm text-stone-600 transition-colors hover:text-stone-900">Horaires</a>
            <a href="/#essai" data-testid="nav-essai" className="text-sm text-stone-600 transition-colors hover:text-stone-900">Séance d&apos;essai</a>
          </nav>
        )}
        
        <div className="flex items-center gap-2">
          <button
            data-testid="theme-toggle"
            aria-label={theme === "dark" ? "Passer en mode clair" : "Passer en mode sombre"}
            onClick={toggleTheme}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-stone-200 text-stone-600 transition-colors hover:border-stone-900 hover:text-stone-900"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" strokeWidth={1.75} /> : <Moon className="h-4 w-4" strokeWidth={1.75} />}
          </button>
          {user ? (
            <>
              {user.role === "admin" ? (
                <>
                  <Button 
                    data-testid="nav-admin" 
                    variant="ghost" 
                    onClick={() => nav("/admin")}
                    className={`font-medium transition-colors ${
                      isActive("/admin") 
                        ? "text-blue-500 hover:text-blue-600" 
                        : "text-white hover:text-stone-200"
                    }`}
                  >
                    Tableau de bord
                  </Button>
                  <Button 
                    data-testid="nav-logout" 
                    onClick={async () => { await logout(); nav("/"); }} 
                    className="rounded-full bg-stone-900 px-6 hover:bg-stone-800"
                  >
                    Déconnexion
                  </Button>
                </>
              ) : (
                <>
                  <Button 
                    data-testid="nav-my-planning" 
                    variant="ghost" 
                    onClick={() => nav("/mon-planning")}
                    className={`font-medium transition-colors ${
                      isActive("/mon-planning") 
                        ? "text-blue-500 hover:text-blue-600" 
                        : "text-white hover:text-stone-200"
                    }`}
                  >
                    Mon Planning
                  </Button>
                  <Button 
                    data-testid="nav-my-space" 
                    variant="ghost" 
                    onClick={() => nav("/mon-espace")}
                    className={`font-medium transition-colors ${
                      isActive("/mon-espace") 
                        ? "text-blue-500 hover:text-blue-600" 
                        : "text-white hover:text-stone-200"
                    }`}
                  >
                    Mes Réservations
                  </Button>
                  <Button 
                    data-testid="nav-logout" 
                    onClick={async () => { await logout(); nav("/"); }} 
                    className="rounded-full bg-red-600 px-6 hover:bg-red-700"
                  >
                    Se déconnecter
                  </Button>
                </>
              )}
            </>
          ) : (
            <Button 
              data-testid="nav-login" 
              onClick={() => nav("/login")} 
              className="rounded-full bg-stone-900 px-6 hover:bg-stone-800"
            >
              Se connecter
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
